import React, { useState } from 'react';
import { useAPIAuth } from '../../providers/APIAuthProvider';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

interface PasswordResetFormProps {
  onSwitchToLogin?: () => void;
}

export const PasswordResetForm: React.FC<PasswordResetFormProps> = ({ onSwitchToLogin }) => {
  const { error, clearError, isLoading } = useAPIAuth();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [validationError, setValidationError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);

    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError('');
    }

    if (error) {
      clearError();
    }
  };

  const validateEmail = (email: string): boolean => {
    if (!email.trim()) {
      setValidationError(t('auth.password_reset.validation_required'));
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setValidationError(t('auth.password_reset.validation_invalid'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEmail(email)) {
      return;
    }

    try {
      // Note: API Auth doesn't have password reset functionality yet
      // This is a placeholder for future implementation
      setIsEmailSent(true);
    } catch (error) {
      // Error is handled by the auth provider
      console.error('Password reset request failed:', error);
    }
  };

  if (isEmailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md w-full space-y-8 p-6">
          <div className="text-center">
            <div className="text-green-500 text-6xl mb-4">✅</div>
            <h2 className="text-3xl font-bold text-muted-foreground mb-2">
              {t('auth.password_reset.success_title')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t('auth.password_reset.success_message')}
            </p>
            <p className="text-primary font-medium mb-8">
              {email}
            </p>
            <p className="text-sm text-muted-foreground mb-8">
              {t('auth.password_reset.success_info')}
            </p>
          </div>

          <div className="space-y-4">
            <Button
              type="button"
              onClick={() => {
                setIsEmailSent(false);
                setEmail('');
              }}
              className="w-full bg-gray-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
            >
              {t('auth.password_reset.try_different_email')}
            </Button>

            {onSwitchToLogin && (
              <Button
                type="button"
                variant="link"
                onClick={onSwitchToLogin}
                className="w-full text-primary hover:text-primary/90 font-medium"
              >
                {t('auth.password_reset.back_to_login')}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full space-y-8 p-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-muted-foreground mb-2">
            {t('auth.password_reset.title')}
          </h2>
          <p className="text-muted-foreground">
            {t('auth.password_reset.subtitle')}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <div className="text-red-500 text-sm">
                  ⚠️ {error}
                </div>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="reset-email" className="block text-sm font-medium text-muted-foreground mb-1">
              {t('auth.password_reset.email_label')}
            </label>
            <Input
              id="reset-email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={handleInputChange}
              disabled={isLoading}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed ${
                validationError ? 'border-red-300' : 'border-input'
              }`}
              placeholder={t('auth.password_reset.email_placeholder')}
            />
            {validationError && (
              <p className="mt-1 text-sm text-red-600">{validationError}</p>
            )}
          </div>

          <div className="space-y-4">
            <Button
              type="submit"
              disabled={isLoading || !email.trim()}
              className="w-full bg-primary text-white px-4 py-3 rounded-lg font-medium hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t('auth.password_reset.submitting')}
                </div>
              ) : (
                t('auth.password_reset.submit')
              )}
            </Button>

            {onSwitchToLogin && (
              <Button
                type="button"
                variant="link"
                onClick={onSwitchToLogin}
                disabled={isLoading}
                className="w-full text-primary hover:text-primary/90 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('auth.password_reset.back_to_login')}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};